import {
  FirebaseFilter,
  FirebaseOrderBy,
  isProject,
  OrganizationType,
  Project,
} from "@snubes/snubes-types";
import { useMemo } from "react";
import { usePaginatedCollection } from "../../Firebase/hooks/usePaginatedCollection";
import { useMyMember } from "../../Members/hooks/useMyMember";
import { useMyUser } from "../../Users/hooks/useMyUser";

interface Args {
  organizationType?: OrganizationType;
  organizationId?: string;
  rowsPerPage: number;
}

export function useProjects(args: Args) {
  const { organizationType, organizationId = null } = args;
  const userId = useMyUser((state) => state.user?.id ?? null);
  const userRole = useMyUser((state) => state.user?.role);
  const memberRole = useMyMember((state) => state.member?.role);

  const isAdmin = userRole === "USER_ADMIN";
  const isOwner = !isAdmin && memberRole === "MEMBER_OWNER";

  // Project-based access control: USER_ADMINs and MEMBER_OWNERs see all
  // projects of the organization. Standard users (MEMBER_MANAGER /
  // MEMBER_REGULAR) only see projects they are assigned to — their queries
  // must filter on assignedUserIds, or the Firestore security rules will
  // reject them.
  const isRestrictedToAssignedProjects = !isAdmin && !isOwner;

  const filters = useMemo<FirebaseFilter<Project>[]>(() => {
    const filters: FirebaseFilter<Project>[] = [
      ["isDeleted", "==", false],
      ["isArchived", "==", false],
    ];

    if (organizationType === "CLIENT") {
      filters.push(["clientOrganizationId", "==", organizationId]);
      return filters;
    }
    if (organizationType === "PROVIDER") {
      filters.push(["providerOrganizationId", "==", organizationId]);
      return filters;
    }

    if (isAdmin) {
      // Admins pass the security rules with any query, so the organization
      // can be matched on both the provider and the client side at once.
      filters.push(["organizationIds", "array-contains", organizationId]);
      return filters;
    }

    if (isRestrictedToAssignedProjects) {
      // Assignment-based query: provable by the security rules and
      // independent of which side (provider/client) the organization is on.
      // The organization is scoped client-side below, because Firestore
      // allows only one array-contains filter per query.
      filters.push(["assignedUserIds", "array-contains", userId]);
      return filters;
    }

    // Owners: both sides are matched via orFilters below.
    return filters;
  }, [
    isAdmin,
    isRestrictedToAssignedProjects,
    userId,
    organizationType,
    organizationId,
  ]);

  // Owners see all projects of their organization. An organization can be
  // the provider of some projects and the client of others (verified in
  // production data), so both sides must be queried. The security rules can
  // prove each OR branch through the respective owner check.
  const orFilters = useMemo<FirebaseFilter<Project>[] | undefined>(() => {
    if (!isOwner || organizationType) return undefined;
    return [
      ["providerOrganizationId", "==", organizationId],
      ["clientOrganizationId", "==", organizationId],
    ];
  }, [isOwner, organizationType, organizationId]);

  const orderBy = useMemo<FirebaseOrderBy<Project>>(
    () => ["createdAt", "desc"],
    [],
  );

  const result = usePaginatedCollection({
    isT: isProject,
    collectionName: "projects",
    filters,
    orFilters,
    orderBy,
    rowsPerPage: args.rowsPerPage,
  });

  // For standard users without an explicit organization type, the
  // organization could not be scoped in the query (see above), so it is
  // scoped here.
  const rows = useMemo(() => {
    if (isRestrictedToAssignedProjects && !organizationType && organizationId) {
      return result.rows.filter((project) =>
        project.organizationIds.includes(organizationId),
      );
    }
    return result.rows;
  }, [
    isRestrictedToAssignedProjects,
    organizationType,
    organizationId,
    result.rows,
  ]);

  return { ...result, rows };
}
